/* eslint-disable no-bitwise */
import { yupResolver } from '@hookform/resolvers/yup';
import { Checkbox, FormControlLabel, FormGroup, FormLabel, Grid, Skeleton, Typography } from '@mui/material';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { AssociationAndDistrictSelect } from 'src/shared/components/organisms/associationAndDistrictSelect';
import { Association } from 'src/shared/models/association/association';
import { District } from 'src/shared/models/district/district';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { useGlobals } from 'src/shared/contexts/appContext';
import { createProfileGroup } from 'src/shared/services/profileGroupService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { toast } from 'react-toastify';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { getUserAssociations, getUserDistricts } from 'src/shared/functions/organizationHelpers';
import { ProfileGroupRequest } from '../models/profileGroupRequest';
import { UserFlagOptions } from './userFlagOptions';

type CreateMemberProfileGroupProps = {
  modalOpen: boolean;
  onClose: () => void;
};

export const CreateMemberProfileGroup = (props: CreateMemberProfileGroupProps) => {
  const { modalOpen, onClose } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useGlobals();
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState<boolean>(false);
  const [association, setAssociation] = useState<Association>();
  const [district, setDistrict] = useState<District>();

  const hasSingleAssociation = getUserAssociations(currentUser);
  const hasSingleDistricts = getUserDistricts(currentUser);

  const userFlagOptions = UserFlagOptions;
  const requiredMessage = t('common:validationMessages.required');
  const maxChar = 50;

  const profileGroupSchema: yup.Schema<ProfileGroupRequest> = yup.object().shape({
    title: yup
      .string()
      .max(maxChar, t('common:validationMessages.maxLength', { count: maxChar }))
      .required(requiredMessage),
    countyAss_id: yup.number().required(),
    localAss_id: yup.number().required(),
    userFlags: yup.number().min(1, t('memberProfile:profileDetail.profileAvailableError')).required(requiredMessage),
    type: yup.number().required(),
  });

  const formMethods = useForm<ProfileGroupRequest>({
    resolver: yupResolver(profileGroupSchema),
    mode: 'all',
    defaultValues: {
      title: '',
      countyAss_id: hasSingleDistricts.length === 1 ? hasSingleDistricts[0].countyAss_id : 0,
      localAss_id: hasSingleAssociation.length === 1 ? hasSingleAssociation[0].localAss_id : 0,
      userFlags: 0,
      type: 0,
    },
  });

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = formMethods;

  const redirect = (id: number) => {
    navigate(`${RoutesEnum.MemberProfileDetail}/${id}`);
  };

  const onSubmit = async (data: ProfileGroupRequest) => {
    setLoading(true);
    try {
      const response = await createProfileGroup(data);
      if (isMounted()) {
        toast.success(<>{t('memberProfile:createProfile.createSuccess')}</>);
        setLoading(false);
        redirect(response.data || 0);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.error(<>{t('memberProfile:createProfile.createError')}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <form>
          <GenericDialog
            disableBackdropClosing
            dialogTitle={t('memberProfile:createProfile.title')}
            onSave={handleSubmit(onSubmit)}
            onClose={() => onClose()}
            open={modalOpen}
            maxWidth='md'
            showDialogActions
            dialogContent={
              <GridWrapper>
                <LoadingOverlay loading={loading} />
                <Grid item xs={12}>
                  <ControlledTextField fullWidth label={t('memberProfile:createProfile.name')} name='title' />
                </Grid>
                <Grid
                  item
                  xs={7}
                  gap={2}
                  sx={{
                    mt: { xs: 1, md: 0 },
                    py: 3,
                    pl: 2,
                    display: 'flex',
                    justifyContent: 'flex-start',
                    '& .MuiAutocomplete-inputRoot': {
                      backgroundColor: 'white',
                    },
                  }}
                >
                  {currentUser ? (
                    <AssociationAndDistrictSelect
                      gridXs={12}
                      gridLg={7}
                      disableDistrict={hasSingleDistricts.length === 1}
                      initialAssociationId={hasSingleAssociation.length === 1 ? hasSingleAssociation[0].localAss_id : undefined}
                      initialDistrictId={hasSingleDistricts.length === 1 ? hasSingleDistricts[0].countyAss_id : undefined}
                      disabled={hasSingleAssociation.length === 1}
                      useGridLayout
                      hideDistrict={currentUser?.districts.length === 0}
                      association={association}
                      district={district}
                      associationCallback={(val) => {
                        setAssociation(val || undefined);
                        setValue('localAss_id', val?.localAss_id || 0);
                        ConsoleHelper.log('associationCallback:  ', val);
                      }}
                      districtCallback={(val) => {
                        setDistrict(val || undefined);
                        setValue('countyAss_id', val?.countyAss_id || 0);
                        ConsoleHelper.log('districtCallback:  ', val);
                      }}
                    />
                  ) : (
                    <Skeleton sx={{ width: '25vw' }} />
                  )}
                </Grid>
                <Grid item xs={12} sm={4} ml={2} mt={-1}>
                  <FormLabel id='label-userFlags'>{t('memberProfile:createProfile.profileAvailable')}</FormLabel>

                  <FormGroup sx={{ ml: 1 }}>
                    {userFlagOptions.map((item) => (
                      <FormControlLabel
                        key={item.value}
                        control={
                          <Checkbox
                            // eslint-disable-next-line no-bitwise
                            checked={(watch('userFlags') & item.value) !== 0}
                            onChange={(e) => {
                              const current = watch('userFlags') || 0;
                              // eslint-disable-next-line no-bitwise
                              const updated = e.target.checked ? current | item.value : current & ~item.value;
                              setValue('userFlags', updated);
                            }}
                          />
                        }
                        label={t(item.label)}
                      />
                    ))}
                  </FormGroup>
                  {errors.userFlags?.message && (
                    <Typography color='error' sx={{ mt: 1 }}>
                      {errors.userFlags.message}
                    </Typography>
                  )}
                </Grid>
              </GridWrapper>
            }
          />
        </form>
      </FormProvider>
    </>
  );
};
